// The Locker stage (Renderer.locker, §14.2) and portrait thumbnails (Renderer.renderPortrait,
// §11.5 awards and locker cards). A cardboard turntable diorama: the bean on a disc with its
// identity ring, a soft studio light with a rim from behind, and a warm gradient backdrop. Equipping
// an item makes the bean react with a preview of it: a pop for looks, a dash with the trail, a
// burst of the KO celebration, a honk, the fall-scream pose, an eye rattle.
import {
  CanvasTexture, Color, CylinderGeometry, DirectionalLight, Group, HemisphereLight, MathUtils, Mesh, NoToneMapping,
  PerspectiveCamera, Scene, SRGBColorSpace, Vector3, WebGLRenderTarget, type WebGLRenderer,
} from 'three';
import { NO_SLOT, TUNE } from '@shared/constants';
import { catalogItem, DEFAULT_COSMETICS, IDENTITIES, type Cosmetics } from '@shared/cosmetics';
import { EV, type SimEvent } from '@shared/events';
import { BS } from '@shared/ids';
import { createBeanView, createFrameView } from '../../game/FrameView';
import { createBlobShadows } from '../entities/BlobShadows';
import { OUTLINE_WIDTH, type MaterialLibrary } from '../materials/library';
import { createBeanRig, type BeanRig } from './BeanRig';
import { CosmeticFx, fxKey } from './cosmeticFx';

const SPIN = { idle: 0.45, damping: 2.5, perPixel: 0.02 } as const;
const BACKDROP = { top: '#FFE7C7', bottom: '#F6C9DE' } as const;
const LOCKER_VIEW = { fov: 30, pos: new Vector3(0, 1.3, 5.2), look: new Vector3(0, 0.82, 0) } as const;
const PORTRAIT_VIEW = { fov: 28, pos: new Vector3(0.4, 1.25, 3.1), look: new Vector3(0.02, 0.9, 0), facing: Math.PI / 2 - 0.42 } as const;
/** How long an equip preview plays (s). */
const PREVIEW_SEC = { trail: 1.4, scream: 1.2, celebration: 2.4 } as const;

export interface LockerStage {
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  setCosmetics(c: Cosmetics, identity: number): void;
  spin(dx: number): void;
  update(dtSec: number): void;
  portrait(webgl: WebGLRenderer, c: Cosmetics, identity: number, size: number): HTMLCanvasElement;
}

/** Linear [0,1] → 8-bit sRGB (render targets stay in linear working space). */
function encodeSrgb(v: number): number {
  const c = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
  return Math.round(MathUtils.clamp(c, 0, 1) * 255);
}

function gradientTexture(top: string, bottom: string): CanvasTexture | null {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = 4;
  c.height = 256;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 4, 256);
  const tex = new CanvasTexture(c);
  tex.colorSpace = SRGBColorSpace;
  return tex;
}

function ev(type: number, a: number, b = NO_SLOT, c = 0, d = 0): SimEvent {
  return { type, tick: 0, a, b, c, d, x: 0, y: 0 };
}

export function createLockerStage(lib: MaterialLibrary): LockerStage {
  const scene = new Scene();
  const backdrop = gradientTexture(BACKDROP.top, BACKDROP.bottom);
  scene.background = backdrop ?? new Color(BACKDROP.top);
  scene.add(new HemisphereLight('#FFF6E9', '#D9A86C', 1.2));
  const key = new DirectionalLight('#FFF2DE', 2.2);
  key.position.set(-0.5, 1, 0.6).normalize().multiplyScalar(10);
  const rim = new DirectionalLight('#FFD6F0', 1.4);
  rim.position.set(0.6, 0.8, -1).normalize().multiplyScalar(10);
  scene.add(key, rim);
  const camera = new PerspectiveCamera(LOCKER_VIEW.fov, 1, 0.1, 50);

  // Cardboard turntable with a dashed rim.
  const stage = new Group();
  const disc = new Mesh(new CylinderGeometry(1.15, 1.22, 0.16, 64), lib.toon({ color: '#E8C48C', gloss: 0.25, pattern: 0 }));
  disc.position.y = -0.08;
  lib.addOutline(disc, OUTLINE_WIDTH.prop);
  const lip = new Mesh(new CylinderGeometry(1.24, 1.26, 0.06, 64), lib.toon({ color: '#C98A4B', gloss: 0.2 }));
  lip.position.y = -0.17;
  lib.addOutline(lip, OUTLINE_WIDTH.prop);
  stage.add(disc, lip);
  scene.add(stage);

  const rig = createBeanRig(lib);
  scene.add(rig.root);
  const bean = createBeanView(0);
  bean.active = true;
  bean.radius = TUNE.bean.radius;
  const view = createFrameView();
  view.beans[0] = bean;
  const shadows = createBlobShadows(lib.blobShadowTexture);
  scene.add(shadows.mesh);

  // Effects for trail / celebration previews (one instance per recipe, built on first use).
  const fxs = new Map<string, CosmeticFx>();
  function fxFor(key: string): CosmeticFx | null {
    const k = fxKey(key);
    if (!k) return null;
    let f = fxs.get(k);
    if (!f) {
      f = new CosmeticFx(k, lib.rampTexture);
      fxs.set(k, f);
      scene.add(f.root);
    }
    return f;
  }

  let cosmetics: Cosmetics = { ...DEFAULT_COSMETICS };
  let identity = 0;
  let angle = 0;
  let vel = 0;
  let time = 0;
  let preview: { kind: 'trail' | 'scream' | 'celebration'; until: number; fx: CosmeticFx | null } | null = null;

  function aim(cam: PerspectiveCamera, v: { fov: number; pos: Vector3; look: Vector3 }, aspect: number): void {
    cam.fov = v.fov;
    cam.aspect = aspect;
    cam.position.copy(v.pos);
    cam.lookAt(v.look);
    cam.updateProjectionMatrix();
  }

  /** Plays the reaction for whatever changed between two looks. */
  function react(prev: Cosmetics, next: Cosmetics): void {
    const r = rig;
    if (next.trail !== prev.trail) {
      const item = catalogItem('trail', next.trail);
      preview = { kind: 'trail', until: time + PREVIEW_SEC.trail, fx: item ? fxFor(item.key) : null };
    } else if (next.celebration !== prev.celebration) {
      const item = catalogItem('celebration', next.celebration);
      const fx = item ? fxFor(item.key) : null;
      fx?.setColor(IDENTITIES[identity].hex);
      fx?.burst(-0.7, 0.2, 0.2);
      preview = { kind: 'celebration', until: time + PREVIEW_SEC.celebration, fx };
      r.onSim?.(ev(EV.KO, 5, 0, 1, 0), view);
    } else if (next.scream !== prev.scream) {
      preview = { kind: 'scream', until: time + PREVIEW_SEC.scream, fx: null };
    } else if (next.honk !== prev.honk) {
      r.onSim?.(ev(EV.HONK, 0), view);
    } else if (next.eyes !== prev.eyes) {
      r.impulse('hit', 1.6, { x: 1, y: 0 });
    } else {
      // A look changed (color, pattern, hat): a happy hop.
      r.onSim?.(ev(EV.ITEM_GET, 0), view);
      r.impulse('squash', 1.2);
    }
  }

  function pose(facing: number, dt: number): void {
    bean.facing = facing;
    bean.state = BS.NORMAL;
    bean.x = 0;
    bean.y = 0;
    bean.z = 0;
    bean.vx = 0;
    bean.vy = 0;
    bean.vz = 0;
    if (preview && time < preview.until) {
      if (preview.kind === 'trail') {
        // A dash on the spot with the trail streaming out behind.
        const sp = TUNE.bean.runSpeed;
        bean.vx = Math.cos(facing) * sp;
        bean.vy = Math.sin(facing) * sp;
        bean.state = (time % 0.7) < 0.15 ? BS.LUNGE : BS.NORMAL;
        preview.fx?.trail(0, 0, 0, bean.vx, 0, bean.vy, dt, 1.4, true);
      } else if (preview.kind === 'scream') {
        bean.state = BS.FALLING;
        bean.z = 0.25;
        bean.vz = -5;
      }
    } else preview = null;
    view.dt = dt;
    view.now = time * 1000;
    rig.update(bean, view, 0);
    for (const f of fxs.values()) f.update(dt);
    shadows.update(view, null);
  }

  // Portraits use their own rig so the locker's animation state is never disturbed.
  let portraitRig: BeanRig | null = null;
  const portraitScene = new Scene();
  portraitScene.add(new HemisphereLight('#FFF6E9', '#D9A86C', 1.2));
  const pKey = new DirectionalLight('#FFF2DE', 2.2);
  pKey.position.set(-0.5, 1, 0.6).normalize().multiplyScalar(10);
  const pRim = new DirectionalLight('#FFD6F0', 1.2);
  pRim.position.set(0.6, 0.8, -1).normalize().multiplyScalar(10);
  portraitScene.add(pKey, pRim);
  const portraitCam = new PerspectiveCamera();
  const portraitBean = createBeanView(0);
  portraitBean.active = true;
  portraitBean.radius = TUNE.bean.radius;
  const portraitView = createFrameView();

  return {
    scene, camera,
    setCosmetics(c, id) {
      const prev = cosmetics;
      const changed = id !== identity || Object.keys(c).some((k) => c[k as keyof Cosmetics] !== prev[k as keyof Cosmetics]);
      cosmetics = { ...c };
      identity = id;
      rig.setLook(c, id, 0, false);
      if (changed) react(prev, c);
    },
    spin(dx) {
      vel += dx * SPIN.perPixel * 60;
    },
    update(dt) {
      const d = Math.min(0.1, Math.max(0, dt));
      time += d;
      vel *= Math.exp(-SPIN.damping * d);
      angle += (SPIN.idle + vel) * d;
      aim(camera, LOCKER_VIEW, camera.aspect);
      pose(Math.PI / 2 - 0.35 + angle, d);
    },
    portrait(webgl, c, id, size) {
      if (!portraitRig) {
        portraitRig = createBeanRig(lib);
        portraitScene.add(portraitRig.root);
      }
      portraitRig.setLook(c, id, 0, false);
      portraitBean.facing = PORTRAIT_VIEW.facing;
      portraitBean.identity = id;
      portraitView.dt = 0;
      portraitRig.update(portraitBean, portraitView, 0);
      aim(portraitCam, PORTRAIT_VIEW, 1);
      // Transparent, multisampled bust shot; the UI places it on any card.
      const target = new WebGLRenderTarget(size, size, { samples: 4 });
      const prevTarget = webgl.getRenderTarget();
      const prevClear = webgl.getClearColor(new Color());
      const prevAlpha = webgl.getClearAlpha();
      const prevTone = webgl.toneMapping;
      webgl.toneMapping = NoToneMapping;
      webgl.setRenderTarget(target);
      webgl.setClearColor(0x000000, 0);
      webgl.clear();
      webgl.render(portraitScene, portraitCam);
      webgl.setRenderTarget(prevTarget);
      webgl.setClearColor(prevClear, prevAlpha);
      webgl.toneMapping = prevTone;
      const px = new Uint8Array(size * size * 4);
      webgl.readRenderTargetPixels(target, 0, 0, size, size, px);
      target.dispose();

      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return canvas;
      const img = ctx.createImageData(size, size);
      for (let y = 0; y < size; y++) {
        const src = (size - 1 - y) * size * 4;
        const dst = y * size * 4;
        for (let x = 0; x < size * 4; x += 4) {
          // Edges are premultiplied by coverage: un-premultiply, then encode to sRGB.
          const a = px[src + x + 3] / 255;
          const k = a > 0 ? 1 / a : 0;
          img.data[dst + x] = encodeSrgb((px[src + x] / 255) * k);
          img.data[dst + x + 1] = encodeSrgb((px[src + x + 1] / 255) * k);
          img.data[dst + x + 2] = encodeSrgb((px[src + x + 2] / 255) * k);
          img.data[dst + x + 3] = px[src + x + 3];
        }
      }
      ctx.putImageData(img, 0, 0);
      return canvas;
    },
  };
}
